import React, { createContext, useContext, useState, useCallback } from "react";

const RandomDataContext = createContext();

/**
 * Hook to access the random data context
 * @returns {Object} The random data context value containing config and functions
 * @throws {Error} If used outside of RandomDataProvider
 */
export const useRandomData = () => {
	const context = useContext(RandomDataContext);
	if (!context) {
		throw new Error("useRandomData must be used within a RandomDataProvider");
	}
	return context;
};

/**
 * Random data provider component that manages random data configuration state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with random data context
 * @returns {JSX.Element} The random data provider component
 */
export const RandomDataProvider = ({ children }) => {
	const [randomDataConfig, setRandomDataConfig] = useState({
		review_cycle: 0,
		billing_admin: 0,
		admin: 0,
		basic_admin: 0,
		employee_tree_count: 0,
		employee_branch_per_tree_count: 0,
		goals: 0,
		review_forms: 0,
		percentage_reviews_with_answers: 0,
		percentage_fully_answered_reviews: 0,
	});

	/**
	 * Update the random data configuration with partial updates
	 * @param {Object} updates - Partial updates to apply to the random data config
	 */
	const updateRandomDataConfig = useCallback((updates) => {
		setRandomDataConfig((prev) => ({ ...prev, ...updates }));
	}, []);

	/**
	 * Reset the random data configuration to default values
	 */
	const resetRandomDataConfig = useCallback(() => {
		setRandomDataConfig({
			review_cycle: 0,
			billing_admin: 0,
			admin: 0,
			basic_admin: 0,
			employee_tree_count: 0,
			employee_branch_per_tree_count: 0,
			goals: 0,
			review_forms: 0,
			percentage_reviews_with_answers: 0,
			percentage_fully_answered_reviews: 0,
		});
	}, []);

	const value = {
		randomDataConfig,
		setRandomDataConfig,
		updateRandomDataConfig,
		resetRandomDataConfig,
	};

	return <RandomDataContext.Provider value={value}>{children}</RandomDataContext.Provider>;
};
